import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import usePlotline from "../../hooks/usePlotline";
import { getPlatform } from "../../utils/config";

import { tokens } from "../../theme";
import WishlinkTypography from "../../WishlinkComponents/WishlinkTypography";
import {
  GA_ACTION,
  GA_CATEGORIES,
  GA_LABEL,
  IMAGE_PREFIX,
  PAGE_URLS,
} from "../../utils/constants";
import { triggerGa } from "../../utils/gaUtils";
import { useGetCreatorDataWithCreatorIdQuery } from "../../Queries/useGetCreatorDataWithCreatorIdQuery";
import {
  Body6MediumTypography,
  Box,
  colorPalette,
  FlexColumn,
  FlexRow,
  Icon,
} from "@wish-ui";

const Footer = ({ bgColor }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: creatorData } = useGetCreatorDataWithCreatorIdQuery();
  const { trackEventPlotline } = usePlotline();

  const { pageName, screenName } = useSelector((state) => state.appReducer);
  const creatorInfo = creatorData || {};

  const handleGaTrigger = (gaLabel) => {
    triggerGa({
      category: GA_CATEGORIES.NAVIGATION,
      action: GA_ACTION.BOTTOM_NAV_CLICK,
      label: gaLabel,
      page: pageName,
      screenName: screenName,
    });
  };

  const handleAnalyticsNavigation = () => {
    handleGaTrigger(GA_LABEL.ANALYTICS);
    if (!localStorage.adminToken && creatorInfo?.show_payout_tnc_screen) {
      navigate(PAGE_URLS.ANALYTICS_TNC);
    } else {
      navigate(`${PAGE_URLS.ANALYTICS}#insights`);
    }
  };

  // const [triggerWebengage] = useWebengageEventTriggerMutation();

  const isWeb = getPlatform() === "web";

  return (
    <Box
      className="fixed footer"
      bottom={0}
      height={isWeb ? 76 : 64}
      zIndex={1000}
      boxShadow="0px -6px 8px -6px rgba(13, 13, 13, 0.08), 0px -8px 16px -6px rgba(13, 13, 13, 0.04)"
      gap={10}
      display="flex"
      justifyContent={isWeb ? "center" : "flex-end"}
      flexDirection="column"
      alignItems="center"
      backgroundColor={bgColor || "#FFF"}
      data-cy="home-footer"
    >
      {/* Your content */}
      <FlexRow px={20} justifyContent="space-between" alignItems="center">
        <FlexColumn
          cursor="pointer"
          id={`footer-home-btn`}
          width={64}
          py="small"
          justifyContent="center"
          alignItems="center"
          gap="mini"
          flexShrink={0}
          onClick={() => {
            handleGaTrigger(GA_LABEL.HOME);
            navigate(PAGE_URLS.HOME);
          }}
        >
          <Icon
            height="16"
            width="16"
            size="14"
            name="fa-solid fa-house-chimney"
            color={
              location.pathname === PAGE_URLS.HOME
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          />
          <Body6MediumTypography
            textAlign="center"
            fontFamily="Lexend"
            color={
              location.pathname === PAGE_URLS.HOME
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          >
            Home
          </Body6MediumTypography>
        </FlexColumn>
        <FlexColumn
          cursor="pointer"
          data-cy={`footer-brands-btn`}
          id={`footer-brands-btn`}
          width={64}
          py="small"
          justifyContent="center"
          alignItems="center"
          gap="mini"
          flexShrink={0}
          onClick={() => {
            handleGaTrigger(GA_LABEL.BRANDS);
            navigate(PAGE_URLS.BRANDS);
          }}
        >
          <Icon
            height="16"
            width="16"
            size="14"
            name="fa-solid fa-compass"
            color={
              location.pathname === PAGE_URLS.BRANDS
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          />
          <Body6MediumTypography
            fontFamily="Lexend"
            textAlign="center"
            color={
              location.pathname === PAGE_URLS.BRANDS
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          >
            Discover
          </Body6MediumTypography>
        </FlexColumn>
        <FlexColumn
          className="footer-create-btn"
          cursor="pointer"
          data-cy="diy-button"
          width={64}
          py="small"
          justifyContent="center"
          alignItems="center"
          gap="mini"
          flexShrink={0}
          onClick={() => {
            handleGaTrigger(GA_LABEL.CREATE);
            trackEventPlotline("create_click", { platform: getPlatform() });
            navigate(PAGE_URLS.CREATE_DIY);
          }}
        >
          <Icon
            height="16"
            width="16"
            size="14"
            name="fa-solid fa-square-plus"
            color={
              location.pathname === PAGE_URLS.CREATE_DIY
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          />
          <Body6MediumTypography
            textAlign="center"
            fontFamily="Lexend"
            color={
              location.pathname === PAGE_URLS.CREATE_DIY
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          >
            Create
          </Body6MediumTypography>
        </FlexColumn>
        <FlexColumn
          className="footer-shop-btn"
          cursor="pointer"
          data-cy={`footer-shop-btn`}
          id={`footer-shop-btn`}
          width={64}
          py="small"
          justifyContent="center"
          alignItems="center"
          gap="mini"
          flexShrink={0}
          onClick={() => {
            handleGaTrigger(GA_LABEL.SHOP);
            navigate(PAGE_URLS.SHOP);
          }}
        >
          <Icon
            height="16"
            width="16"
            size="14"
            name="fa-solid fa-shop"
            color={
              location.pathname === PAGE_URLS.SHOP
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          />
          <Body6MediumTypography
            textAlign="center"
            fontFamily="Lexend"
            color={
              location.pathname === PAGE_URLS.SHOP
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          >
            Shop
          </Body6MediumTypography>
        </FlexColumn>
        <FlexColumn
          className="footer-analytics-btn"
          cursor="pointer"
          data-cy={`footer-analytics-btn`}
          id={`footer-analytics-btn`}
          width={64}
          py="small"
          justifyContent="center"
          alignItems="center"
          gap="mini"
          flexShrink={0}
          onClick={handleAnalyticsNavigation}
        >
          <Icon
            height="16"
            width="16"
            size="14"
            name="fa-solid fa-chart-pie"
            color={
              location.pathname === PAGE_URLS.ANALYTICS
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          />
          <Body6MediumTypography
            fontFamily="Lexend"
            textAlign="center"
            color={
              location.pathname === PAGE_URLS.ANALYTICS
                ? colorPalette.popOrange[500]
                : colorPalette.popBlack[100]
            }
          >
            Analytics
          </Body6MediumTypography>
        </FlexColumn>
      </FlexRow>
    </Box>
  );
};

export default Footer;
